import React, { useEffect, useRef, useState } from "react";
import { isElementInViewport, observeElementIntersection } from "@/utils/intersection-observer";
import "@/styles/lazy-poster-image.css";

var PLACEHOLDER_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

type LazyPosterImageProps = {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  eager?: boolean;
};

export default function LazyPosterImage({
  src,
  alt,
  className,
  width = 180,
  height = 270,
  eager = false,
}: LazyPosterImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(eager ? src : null);

  useEffect(function () {
    if (eager) {
      setLoadedSrc(src);
      return;
    }

    var img = imgRef.current;
    if (!img) {
      return;
    }

    var loaded = false;
    var margin = 30;
    var timers: number[] = [];

    function loadImage() {
      if (loaded) {
        return;
      }
      loaded = true;
      var dataSrc = img.getAttribute("data-src");
      setLoadedSrc(dataSrc ? dataSrc : src);
    }

    function tryLoadIfVisible() {
      if (loaded) {
        return;
      }
      if (isElementInViewport(img, margin)) {
        loadImage();
      }
    }

    var disconnect = observeElementIntersection(
      img,
      function (isIntersecting) {
        if (isIntersecting) {
          loadImage();
        }
      },
      { rootMargin: margin + "px" }
    );

    timers = [100, 400, 1000, 2000, 4000].map(function (delay) {
      return window.setTimeout(tryLoadIfVisible, delay);
    });

    timers.push(
      window.setTimeout(function () {
        loadImage();
      }, 6000)
    );

    return function () {
      disconnect();
      timers.forEach(function (timerId) {
        window.clearTimeout(timerId);
      });
    };
  }, [src, eager]);

  var rootClass = "lazy-poster-image";
  if (className) {
    rootClass += " " + className;
  }
  if (loadedSrc) {
    rootClass += " lazy-poster-image--loaded";
  }

  return (
    <img
      ref={imgRef}
      data-src={src}
      src={loadedSrc ? loadedSrc : PLACEHOLDER_SRC}
      alt={alt}
      width={width}
      height={height}
      className={rootClass}
    />
  );
}
